// Dialog to display product selection conditions from the selection rule
import type { ReactNode } from "react";

import {
  areProductConditionsMet,
  cartItemMatchesCondition,
  computeOrderConditionStatus,
  computeProductConditionStatuses,
  conditionsUseProductMetadata,
  type ProductConditionStatus,
  type SelectionRule as BaseSelectionRule,
  type SelectionRuleCondition,
} from "@/lib/conditions";
import {
  calculateCartQuantity,
  calculateCartTotal,
  exceedsMaxPrice,
  exceedsMaxQuantity,
  formatPrice,
  meetsMinQuantity,
  type CartItem,
} from "@/lib/queries/products";
import {
  Badge,
  Button,
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
  ScrollArea,
  Separator,
  useIsMobile,
} from "@repo/ui";
import { cn } from "@repo/ui/lib/utils";
import {
  CheckCircle2,
  Clock,
  DollarSign,
  Hash,
  Info,
  Layers,
  Package,
  Tag,
} from "lucide-react";

/**
 * @deprecated Import directly from `@/lib/conditions` instead.
 * These re-exports exist only for backward compatibility.
 */
export type { SelectionRuleCondition, ProductConditionStatus };
/** @deprecated Import directly from `@/lib/conditions` instead. */
export {
  areProductConditionsMet,
  cartItemMatchesCondition,
  computeOrderConditionStatus,
  computeProductConditionStatuses,
  conditionsUseProductMetadata,
};

interface SelectionRuleSettings {
  showDescription: boolean;
}

interface SelectionRule extends BaseSelectionRule {
  label?: string | null;
  description?: string | null;
  settings?: SelectionRuleSettings | null;
}

interface ConditionsDialogProps {
  selectionRule: SelectionRule;
  currencyCode?: string;
  showPrice?: boolean;
  cart?: CartItem[];
  triggerClassName?: string;
  isShopifyType?: boolean;
}

export function ConditionsDialog({
  selectionRule,
  currencyCode = "USD",
  showPrice = true,
  cart = [],
  triggerClassName,
  isShopifyType = false,
}: ConditionsDialogProps) {
  const hasConditions =
    selectionRule.conditions && selectionRule.conditions.length > 0;
  const hasGlobalRules =
    selectionRule.minQuantity ||
    selectionRule.maxQuantity ||
    (showPrice && selectionRule.maxPrice);

  if (!hasConditions && !hasGlobalRules && !selectionRule.description) {
    return null;
  }

  const orderStatus = computeOrderConditionStatus(cart, selectionRule);
  const conditionStatuses = computeProductConditionStatuses(
    cart,
    selectionRule.conditions ?? [],
  );
  const cartTotal = calculateCartTotal(cart);
  const cartQty = calculateCartQuantity(cart);
  const isMobile = useIsMobile();
  const ScrollContainer = isMobile ? "div" : ScrollArea;
  const showDescription = selectionRule.settings?.showDescription ?? true;

  return (
    <ResponsiveDialog>
      <ResponsiveDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn("gap-1.5 min-w-[130px]", triggerClassName)}
        >
          <Info className="h-3.5 w-3.5" />
          View Conditions
        </Button>
      </ResponsiveDialogTrigger>
      <ResponsiveDialogContent className="sm:max-w-md">
        <ResponsiveDialogHeader>
          <ResponsiveDialogTitle>Selection Rules</ResponsiveDialogTitle>
          {selectionRule.description && showDescription && (
            <ResponsiveDialogDescription>
              {selectionRule.description}
            </ResponsiveDialogDescription>
          )}
        </ResponsiveDialogHeader>

        <ScrollContainer
          className={isMobile ? "max-h-[80vh] overflow-y-auto" : "max-h-[80vh]"}
        >
          <div className="space-y-4 pr-4">
            {/* Green banner when all conditions met */}
            {orderStatus.fulfilled && (
              <div className="flex items-center gap-2 p-3 bg-success/50 border border-success-foreground/20 rounded-lg">
                <CheckCircle2 className="h-4 w-4 text-success-foreground shrink-0" />
                <p className="text-sm font-medium text-success-foreground">
                  Your cart meets all the requirements.
                </p>
              </div>
            )}

            {/* Shopify warning: conditions use metadata unavailable for Shopify products */}
            {isShopifyType &&
              conditionsUseProductMetadata(selectionRule.conditions ?? []) && (
                <div className="flex items-start gap-2 p-3 bg-warning/50 border border-warning-foreground/20 rounded-lg">
                  <Info className="h-4 w-4 text-warning-foreground shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-warning-foreground">
                      Limited condition matching
                    </p>
                    <p className="text-xs text-warning-foreground mt-0.5">
                      Some product conditions may not evaluate correctly.
                      Shopify products don&apos;t include category, type,
                      vendor, or tag metadata needed for matching.
                    </p>
                  </div>
                </div>
              )}

            {/* Order Conditions */}
            {hasGlobalRules && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-medium text-foreground">
                    Order Conditions
                  </h4>
                  <StatusBadge
                    fulfilled={orderStatus.fulfilled}
                    hasCart={cart.length > 0}
                  />
                </div>
                <div className="space-y-1.5">
                  {selectionRule.minQuantity != null && (
                    <OrderConditionRow
                      icon={
                        <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      }
                      fulfilled={meetsMinQuantity(
                        cart,
                        selectionRule.minQuantity,
                      )}
                      hasCart={cart.length > 0}
                    >
                      Minimum{" "}
                      <span className="font-medium">
                        {selectionRule.minQuantity}
                      </span>{" "}
                      {selectionRule.minQuantity === 1 ? "item" : "items"}
                      {cart.length > 0 && (
                        <span className="text-muted-foreground ml-1">
                          ({cartQty} selected)
                        </span>
                      )}
                    </OrderConditionRow>
                  )}
                  {selectionRule.maxQuantity != null && (
                    <OrderConditionRow
                      icon={
                        <Package className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      }
                      fulfilled={
                        !exceedsMaxQuantity(cart, selectionRule.maxQuantity)
                      }
                      hasCart={cart.length > 0}
                    >
                      Maximum{" "}
                      <span className="font-medium">
                        {selectionRule.maxQuantity}
                      </span>{" "}
                      {selectionRule.maxQuantity === 1 ? "item" : "items"}
                      {cart.length > 0 && (
                        <span className="text-muted-foreground ml-1">
                          ({cartQty} selected)
                        </span>
                      )}
                    </OrderConditionRow>
                  )}
                  {showPrice && selectionRule.maxPrice != null && (
                    <OrderConditionRow
                      icon={
                        <DollarSign className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      }
                      fulfilled={!exceedsMaxPrice(cart, selectionRule.maxPrice)}
                      hasCart={cart.length > 0}
                    >
                      Maximum total:{" "}
                      <span className="font-medium">
                        {formatPrice(selectionRule.maxPrice, currencyCode)}
                      </span>
                      {cart.length > 0 && (
                        <span className="text-muted-foreground ml-1">
                          ({formatPrice(cartTotal, currencyCode)} in cart)
                        </span>
                      )}
                    </OrderConditionRow>
                  )}
                </div>
              </div>
            )}

            {/* Product Conditions */}
            {hasConditions && (
              <>
                {hasGlobalRules && <Separator />}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-foreground">
                    Product Conditions
                  </h4>
                  {/* Show match mode if there are multiple conditions */}
                  {selectionRule.conditions &&
                    selectionRule.conditions.length > 1 &&
                    selectionRule.conditions[0]?.conditionOperation && (
                      <p className="text-xs text-muted-foreground">
                        Products must match{" "}
                        <span className="font-medium">
                          {selectionRule.conditions[0].conditionOperation.toLowerCase() ===
                          "and"
                            ? "all"
                            : "any"}
                        </span>{" "}
                        of the following conditions
                      </p>
                    )}
                  {selectionRule.conditions?.map((condition, index) => {
                    const status = conditionStatuses[index];
                    return (
                      <ConditionCard
                        key={condition.id ?? `condition-${index}`}
                        condition={condition}
                        currencyCode={currencyCode}
                        showPrice={showPrice}
                        matchingQuantity={status?.matchingQuantity ?? 0}
                        fulfilled={status?.fulfilled ?? false}
                        triggered={status?.triggered ?? false}
                        hasCart={cart.length > 0}
                      />
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </ScrollContainer>
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  );
}

export function StatusBadge({
  fulfilled,
  hasCart,
}: {
  fulfilled: boolean;
  hasCart: boolean;
}) {
  if (!hasCart) {
    return (
      <Badge variant="secondary" className="text-xs px-1.5 py-0 gap-1">
        <Clock className="h-3 w-3" aria-hidden="true" />
        Pending
      </Badge>
    );
  }
  if (fulfilled) {
    return (
      <Badge variant="success" className="text-xs px-1.5 py-0 gap-1">
        <CheckCircle2 className="h-3 w-3" aria-hidden="true" />
        Fulfilled
      </Badge>
    );
  }
  return (
    <Badge variant="warning" className="text-xs px-1.5 py-0 gap-1">
      <Clock className="h-3 w-3" aria-hidden="true" />
      Pending
    </Badge>
  );
}

function OrderConditionRow({
  icon,
  fulfilled,
  hasCart,
  children,
}: {
  icon: ReactNode;
  fulfilled: boolean;
  hasCart: boolean;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {icon}
      <span className="flex-1">{children}</span>
      {hasCart &&
        (fulfilled ? (
          <CheckCircle2
            className="h-3.5 w-3.5 text-success-foreground shrink-0"
            aria-hidden="true"
          />
        ) : (
          <Clock
            className="h-3.5 w-3.5 text-warning-foreground shrink-0"
            aria-hidden="true"
          />
        ))}
    </div>
  );
}

export function ConditionCard({
  condition,
  currencyCode,
  showPrice = true,
  matchingQuantity,
  fulfilled,
  triggered,
  hasCart,
}: {
  condition: SelectionRuleCondition;
  currencyCode: string;
  showPrice?: boolean;
  matchingQuantity: number;
  fulfilled: boolean;
  triggered: boolean;
  hasCart: boolean;
}) {
  const quantityLabel = condition.quantityType === "MINIMUM" ? "Min" : "Max";

  const borderClass = hasCart
    ? fulfilled
      ? "border-success-foreground/20"
      : condition.isRequired || triggered
        ? "border-warning-foreground/20"
        : "border-border"
    : "border-border";

  return (
    <div className={`rounded-lg border p-3 space-y-2 ${borderClass}`}>
      <div className="flex items-center justify-between">
        <h5 className="text-sm font-medium">{condition.conditionName}</h5>
        <div className="flex items-center gap-1.5">
          <Badge
            variant={condition.isRequired ? "destructive" : "secondary"}
            className="text-xs px-1.5 py-0"
          >
            {condition.isRequired ? "Required" : "Optional"}
          </Badge>
          <Badge variant="secondary" className="text-xs px-1.5 py-0">
            {quantityLabel}: {condition.quantity}
          </Badge>
          {hasCart && <StatusBadge fulfilled={fulfilled} hasCart={hasCart} />}
        </div>
      </div>

      {/* Show matching quantity progress when cart has items */}
      {hasCart && (condition.isRequired || triggered) && (
        <p className="text-xs text-muted-foreground">
          {matchingQuantity} matching{" "}
          {matchingQuantity === 1 ? "item" : "items"} in cart
          {condition.quantityType === "MINIMUM"
            ? ` (need ${condition.quantity})`
            : ` (max ${condition.quantity})`}
        </p>
      )}

      <div className="space-y-1">
        {/* Vendors */}
        {condition.vendors && condition.vendors.length > 0 && (
          <ConditionDetail
            icon={<Tag className="h-3 w-3" />}
            label="Vendors"
            values={condition.vendors}
          />
        )}

        {/* Product Types */}
        {condition.productTypes && condition.productTypes.length > 0 && (
          <ConditionDetail
            icon={<Layers className="h-3 w-3" />}
            label="Product Types"
            values={condition.productTypes}
          />
        )}

        {/* Categories */}
        {condition.categories && condition.categories.length > 0 && (
          <ConditionDetail
            icon={<Hash className="h-3 w-3" />}
            label="Categories"
            values={condition.categories}
          />
        )}

        {/* Tags */}
        {condition.tags && condition.tags.length > 0 && (
          <ConditionDetail
            icon={<Tag className="h-3 w-3" />}
            label="Tags"
            values={condition.tags}
          />
        )}

        {/* Collections */}
        {condition.collections && condition.collections.length > 0 && (
          <ConditionDetail
            icon={<Layers className="h-3 w-3" />}
            label="Collections"
            values={condition.collections.map((c) => c.name)}
          />
        )}

        {/* Products */}
        {condition.products && condition.products.length > 0 && (
          <ConditionDetail
            icon={<Package className="h-3 w-3" />}
            label="Products"
            values={condition.products.map((p) => p.name)}
          />
        )}

        {/* Options */}
        {condition.options && condition.options.length > 0 && (
          <div className="text-xs text-muted-foreground">
            {condition.options.map((opt) => (
              <div key={opt.name} className="flex items-start gap-1.5 mt-1">
                <Tag className="h-3 w-3 mt-0.5 shrink-0" />
                <span>
                  {opt.name}: {opt.values.join(", ")}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Price Range */}
        {showPrice &&
          (condition.priceMin != null || condition.priceMax != null) && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <DollarSign className="h-3 w-3 shrink-0" />
              <span>
                Price:{" "}
                {condition.priceMin != null
                  ? formatPrice(condition.priceMin, currencyCode)
                  : "Any"}{" "}
                -{" "}
                {condition.priceMax != null
                  ? formatPrice(condition.priceMax, currencyCode)
                  : "Any"}
              </span>
            </div>
          )}
      </div>
    </div>
  );
}

function ConditionDetail({
  icon,
  label,
  values,
}: {
  icon: ReactNode;
  label: string;
  values: string[];
}) {
  return (
    <div className="flex items-start gap-1.5 text-xs text-muted-foreground">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>
        {label}: {values.join(", ")}
      </span>
    </div>
  );
}
